import { useContext, useEffect, useMemo, useState } from 'react';
import {
  BefGlobalContext,
  BefRpcContext,
  BefUserServiceContext,
} from '@beflam/bef-core';
import { useNavigate } from 'react-router-dom';
import { getUrlQueryParams } from '../renderer/utils';
import '@alifd/next/lib/tab/style';
import '@alifd/next/lib/checkbox/style';
import Tab from '@alifd/next/lib/tab';
import Checkbox from '@alifd/next/lib/checkbox';
import './Login.css';

export function Login() {
  const authStorageKey = 'bef-auth-mode';
  const userStorageKey = 'bef-user-message';
  const navigate = useNavigate();
  const userService = useContext(BefUserServiceContext);
  const glbalService = useContext(BefGlobalContext);
  const rpcService = useContext(BefRpcContext);

  // 过滤掉 OAuth 相关参数的辅助函数
  const filterOAuthParams = (queryString: string): string => {
    if (!queryString) return '';
    const params = new URLSearchParams(queryString);
    // 删除 OAuth 相关参数
    params.delete('authid');
    params.delete('code');
    params.delete('state');
    const filtered = params.toString();
    return filtered ? '?' + filtered : '';
  };

  let extendUrl = window.location.search;
  let commandUrlQuery = getUrlQueryParams(extendUrl);
  //初始的时候,需要获取当前的登录方式
  //存储最后一次的登录名称
  const isAgentMode = !!(commandUrlQuery?.agentmode && commandUrlQuery.agentmode !== '');
  const [isAgentLoading, setIsAgentLoading] = useState<boolean>(isAgentMode);
  const [authMode, setAuthMode] = useState<any>(null);
  const [authModes, setAuthModes] = useState<any[]>([{ AuthId: '' }]);

  // 获取当前authModes中AuthType不是OAuth或为空的项
  const filteredAuthModes = useMemo(() => {
    return authModes.filter((authItem) => {
      // 如果authItem为null或undefined,保留它
      if (!authItem) return true;
      // 如果没有AuthType属性,保留它
      if (!authItem.AuthType) return true;
      // 如果AuthType不是OAuth,保留它
      return authItem.AuthType !== 'OAuth' && authItem.AuthType !== 'WeworkAuth' && authItem.AuthType !== 'FeishuAuth';
    });
  }, [authModes]);

  const oauthModes = useMemo(() => {
    return authModes.filter((authItem) => {
      return authItem && authItem.AuthType === 'OAuth';
    });
  }, [authModes]);

  useEffect(() => {
    if (commandUrlQuery) {
      const appId: string = commandUrlQuery?.appid || commandUrlQuery?.appId
      if (appId && appId.length > 0) glbalService.currentAppId = appId;
      if (commandUrlQuery?.sessionSource) glbalService.rpcDirect = true;
    }
    //由于可以通过反向代理来获取当前的AppId，所以这里不强制要求必须传入AppId
    //配置记住的用户名密码
    const userStorageValue = localStorage.getItem(userStorageKey);
    if (userStorageValue) {
      const userStorageObject = JSON.parse(userStorageValue);
      if (userStorageObject && userStorageObject.ischecked) {
        setIschecked(true);
        setUsername(userStorageObject.username || '');
        setPassword(userStorageObject.password || '');
      }
    }

    const authStorageValue = localStorage.getItem(authStorageKey);
    let savedAuthMode: any = null;
    if (authStorageValue) {
      let authStorageObject = JSON.parse(authStorageValue);
      savedAuthMode = authStorageObject[glbalService.currentAppId];
      setAuthMode(savedAuthMode);
    }
    //这里加null的主要作用是添加主账号登录，设置为null代表取消原来的AuthMode，并设置为主账号登录模式
    rpcService
      .callRpcAsync<any>(
        glbalService.currentAppId,
        'Beflam.Framework',
        'Beflam.Framework.Service.ConfigService',
        'ClientInit',
        {
          locationHref: window.location.href,
          clientId: glbalService.clientId,
          sessionId: commandUrlQuery?.sessionId,
          sessionSource: commandUrlQuery?.sessionSource,
          authId: commandUrlQuery?.authid,
        }
      )
      .then((result) => {
        if (isAgentMode) setIsAgentLoading(false);
        if (result && result.Auth && result.Auth.Auths) {
          setAuthModes([...result.Auth.Auths, { AuthId: '' }]);
          // 使用本地变量 savedAuthMode 而不是 state 中的 authMode
          if (savedAuthMode == null || savedAuthMode === undefined) setAuthMode(authModes[0].AuthId);
        }
        if (result.AppId) glbalService.currentAppId = result.AppId;
        setAppname(result.AppName || 'BeflamWeb');
        //如果存在SessionId，说明已经登录成功，直接跳转
        if (result.SessionId) {
          userService.setSession(result);
          const cleanUrl = filterOAuthParams(extendUrl);
          navigate('/' + cleanUrl);
        } else if (result.OAuthUrl && result.OAuthUrl.url) {
          window.location.replace(result.OAuthUrl.url);
        }
      })
      .catch((error) => {
        if (isAgentMode) setIsAgentLoading(false);
        if (error.message) setErrorMsg(error.message);
        else setErrorMsg(error);
      });
  }, []);

  const [appname, setAppname] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [ischecked, setIschecked] = useState<any>(false);
  const [language, setLanguage] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const login = () => {
    if (!username) {
      setErrorMsg('username');
      return;
    }
    if (!password) {
      setErrorMsg('password');
      return;
    }

    //目前从url中获取当前的appId
    if (!glbalService.currentAppId) glbalService.currentAppId = commandUrlQuery?.appid || commandUrlQuery?.appId;
    if (!glbalService.currentAppId) {
      setErrorMsg("The AppId can't be empty.");
      return;
    }

    userService
      .login({
        appId: glbalService.currentAppId,
        userId: username,
        password: password,
        language: language,
        authMode: authMode,
      })
      .subscribe(
        (result) => {
          //如果返回了AppId，就用这个返回的AppId，原因是可能服务端限定了AppId
          if (result.data.AppId) {
            glbalService.currentAppId = result.data.AppId;
          }
          const cleanUrl = filterOAuthParams(extendUrl);
          navigate('/' + cleanUrl);
        },
        (err) => {
          setErrorMsg(err.message);
        }
      );

    //保存用户名密码
    let userStorageValue = localStorage.getItem(userStorageKey);
    if (!userStorageValue) userStorageValue = '{}';
    const userStorageObject = JSON.parse(userStorageValue);
    userStorageObject.ischecked = ischecked;
    userStorageObject.username = ischecked ? username : '';
    //如果没有加密解密功能，就不保存密码
    localStorage.setItem(userStorageKey, JSON.stringify(userStorageObject));
  };

  const authChangeClick = (authmode: string | Number) => {
    let authMode: any = authmode;
    //目前主账号登录用空表示
    if (authMode === '主账号登录') authMode = "";
    setAuthMode(authMode);
    let authStorageValue = localStorage.getItem(authStorageKey);
    if (!authStorageValue) authStorageValue = '{}';
    let authStorageObject = JSON.parse(authStorageValue);
    authStorageObject[glbalService.currentAppId] = authMode;
    localStorage.setItem(authStorageKey, JSON.stringify(authStorageObject));
  };

  const changeName = (args: any) => {
    setUsername(args.currentTarget.value);
  };
  const changePW = (args: any) => {
    setPassword(args.currentTarget.value);
  };
  const changeLanguage = (args: any) => {
    setLanguage(args.currentTarget.value);
  };
  const onCheckChange = (args: any) => {
    setIschecked(!ischecked);
  };

  const handleOAuthLogin = (oauthItem: any) => {
    // 确保有 AuthId
    if (!oauthItem.AuthId) {
      setErrorMsg('OAuth 配置错误');
      return;
    }

    // 获取完整的当前 URL(包括 query string)
    const currentUrl = window.location.href;

    // 调用 RPC 服务获取 OAuth 跳转地址
    rpcService
      .callRpcAsync<any>(
        glbalService.currentAppId,
        'Beflam.Framework',
        'Beflam.Framework.Service.ConfigService',
        'GetOAuthUrl',
        {
          authId: oauthItem.AuthId,
          redirectUrl: currentUrl
        }
      )
      .then((result) => {
        if (result && result.url) {
          // 跳转到 OAuth 认证页面
          window.location.href = result.url;
        } else {
          setErrorMsg('未能获取 OAuth 登录地址');
        }
      })
      .catch((error) => {
        if (error.message) setErrorMsg(error.message);
        else setErrorMsg('OAuth 登录失败');
      });
  };

  if (isAgentMode && isAgentLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="bef-agent-loading">
          <i className="fa fa-spinner fa-spin fa-2x"></i>
        </div>
      </div>
    );
  }

  return (
    <div
      className="middle-box text-center loginscreen animated fadeInDown"
      style={{ marginTop: '10%' }}
    >
      <div>
        <h3>
          <b>{appname}</b>
        </h3>
        {errorMsg && <span className="loginbox_errinfo">{errorMsg}</span>}
        <form id="login-form" className="m-t">
          {filteredAuthModes && filteredAuthModes.length > 1 && (
            <Tab
              activeKey={authMode || '主账号登录'}
              onChange={authChangeClick}
            >
              {filteredAuthModes.map((authItem) => {
                const selected = authItem.AuthId === authMode;
                return (
                  <Tab.Item
                    title={authItem.AuthId || '主账号登录'}
                    key={authItem.AuthId || '主账号登录'}
                    active={selected}
                  ></Tab.Item>
                );
              })}
            </Tab>
          )}

          <div className="m-t"></div>
          <div className="form-group">
            <input
              type="email"
              id="username"
              name="username"
              className="form-control"
              placeholder="用户名"
              value={username}
              onChange={changeName}
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              id="password"
              name="password"
              className="form-control"
              placeholder="密码"
              value={password}
              onChange={changePW}
            />
          </div>
          <div className="form-group">
            <select
              name="language"
              className="form-control"
              onChange={changeLanguage}
            >
              <option value="">默认(default)</option>
              <option value="zh-CN">中文</option>
              <option value="en-US">英文</option>
            </select>
          </div>
          <div className="login-check">
            <Checkbox checked={ischecked} onChange={onCheckChange}>
              记住密码
            </Checkbox>
          </div>
          <button
            type="button"
            className="btn btn-primary block full-width m-b"
            onClick={login}
          >
            登录
          </button>
        </form>

        {/* OAuth 登录选项 */}
        {oauthModes && oauthModes.length > 0 && (
          <div className="oauth-login-section">
            {oauthModes.map((oauthItem, index) => (
              <div
                key={oauthItem.AuthId || index}
                className="oauth-login-item"
                onClick={() => handleOAuthLogin(oauthItem)}
              >
                <div className="oauth-icon">
                  {oauthItem.AuthData?.Avatar ? (
                    <img
                      src={oauthItem.AuthData.Avatar}
                      alt="OAuth Provider"
                    />
                  ) : (
                    <i className="fa fa-sign-in"></i>
                  )}
                </div>
                <div className="oauth-text">
                  {oauthItem.AuthData?.Name || oauthItem.AuthId}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* <TooltipComponent  content={'errorMsg'} opensOn={'Custom'} position='BottomCenter' className="password" >
            <p className="text-muted text-center"> Version 1.0.0 </p>
          </TooltipComponent> */}
      </div>
    </div>
  );
}
